<template>
  <div class="Bookings">
    <div class="title">
      <h2 class="light">
        Bookings
      </h2>

      <div class="filters">
        <div class="status-container">
          <select
            class="status"
            v-model="status">
            <option
              v-for="s of statuses"
              :value="s.value"
              :key="s.value">
              {{ s.name }}
            </option>
          </select>
          <Tabs
            class="status"
            :options="tabs"
            v-model="status" />
        </div>

        <select
          class="location"
          v-model="locationId">
          <option
            v-for="l of ptLocationList"
            :value="l.id"
            :key="l.id">
            {{ l.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="search">
      <form @submit.prevent="handleSearch()">
        <input
          v-model="search"
          type="search"
          placeholder="Search">
        <Icon
          v-if="!search"
          name="search" />
        <span @click="clearSearch">
          <Icon
            v-if="search"
            class="clear-search"
            name="close" />
        </span>
      </form>
    </div>

    <transition-group
      tag="div"
      class="bookings-list"
      name="list">
      <Booking
        v-for="booking of searchedBookingsList"
        :key="booking.id"
        @bookings-updated="handleBookingsUpdated"
        :booking="booking" />
    </transition-group>

    <div class="center">
      <div v-if="!hasMore && working">
        <LoadingIcon />
      </div>
      <div v-if="empty">
        No results
      </div>
    </div>

    <div class="load">
      <LoadingButton
        v-if="hasMore"
        icon="bars"
        :loading="working"
        @click="loadMore">
        Load more
      </LoadingButton>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import Booking from "@/components/Booking";
import Tabs from "@/components/Tabs";
import LoadingIcon from "@/icons/LoadingIcon";
import LoadingButton from "@/components/LoadingButton";
// import debounce from "lodash/debounce";

const PAGESIZE = 20;

export default {
  components: { Booking, Tabs, LoadingButton, LoadingIcon },
  data: function () {
    return {
      search: "",
      q: "",
      locationId: null,
      nodes: [],
      status: "NEW",
      statuses: [
        { name: "New", value: "NEW" },
        { name: "Assigned", value: "ASSIGNED" },
        { name: "Delivered", value: "DELIVERED" },
        { name: "Cancelled", value: "CANCELLED" },
        { name: "Archived", value: "ARCHIVED" },
      ],
      loading: 0,
      first: PAGESIZE,
      offset: 0,
      hasMore: true,
      counts: {
        new: { totalCount: 0 },
        assigned: { totalCount: 0 },
        delivered: { totalCount: 0 },
        cancelled: { totalCount: 0 },
        archived: { totalCount: 0 },
      }
    };
  },

  watch: {
    status() {
      this.clearSearch();
      this.offset = 0;
      this.nodes.splice(0);
    },
    ptLocationList(newVal, oldVal) {
      if (!oldVal) {
        if (newVal.length > 0) {
          this.locationId = newVal[0].id;
        }
      }
    },
  },
  computed: {
    tabs() {
      return this.statuses.map(s => {
        return {
          ...s,
          count: this.counts[s.name.toLowerCase()],
        }
      });
    },
    empty() {
      return !this.working && this.searchedBookingsList.length === 0;
    },

    ptId() {
      return this.$store.getters["profile"].id;
    },

    working() {
      return this.loading > 0;
    },

    searchedBookingsList() {
      return this.nodes;
    },
  },

  methods: {
    handleSearch() {
      this.nodes = [];
      this.hasMore = false;
      this.q = this.search;
    },
    clearSearch() {
      this.search = "";
      this.q = "";
    },
    handleBookingsUpdated(ev) {
      ev.filter(b => b.status !== this.status)
        .forEach(b => {
          const idx = this.nodes.findIndex(n => n.id === b.id);
          if (idx > -1) {
            this.nodes.splice(idx, 1);
          }
      });
      this.$apollo.queries.counts.refetch();
    },

    loadMore() {
      this.offset += PAGESIZE;
      this.$nextTick(() => {
        this.$apollo.queries.bookingsList.fetchMore({
          variables: {
            offset: this.offset,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const nodes = fetchMoreResult.bookingsList;
            this.nodes.push(...nodes);
            if (nodes.length !== PAGESIZE) {
              this.hasMore = false;
            } else {
              this.hasMore = true;
            }
            return {
              __typename: previousResult.__typename,
              bookingsList: [
                ...previousResult.bookingsList,
                ...nodes,
              ],
            };
          },
        });
      });
    },
  },

  apollo: {
    bookingsList: {
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
      loadingKey: "loading",
      update(data) {
        if (this.q) {
          this.nodes = data.bookingSearchList;
          this.hasMore = false;
        } else {
          this.nodes = data.bookingsList;
          if (data.bookingsList.length !== PAGESIZE) {
            this.hasMore = false;
          } else {
            this.hasMore = true;
          }
        }
        return data.bookings;
      },
      query() {
        if (this.q) {
          return bookingSearchQuery;
        }
        return bookingQuery;
      },
      variables() {
        return {
          first: PAGESIZE,
          offset: 0,
          status: this.status,
          ptId: ["NEW", "ARCHIVED"].indexOf(this.status) > -1 ? null : this.ptId,
          q: this.q,
          locationId: this.locationId,
        };
      },
      pollInterval: 0, // disabled
      skip() {
        return this.locationId === null;
      },
    },
    counts: {
      fetchPolicy: "no-cache",
      query: gql`
          query MyBookings($ptId: UUID!, $locationId: UUID!) {
            new: bookings(condition: { status: "NEW", locationId: $locationId }) {
              totalCount
            }
            assigned: bookings(condition: { status: "ASSIGNED", ptId: $ptId, locationId: $locationId }) {
              totalCount
            }
            delivered: bookings(condition: { status: "DELIVERED", ptId: $ptId, locationId: $locationId }) {
              totalCount
            }
            cancelled: bookings(condition: { status: "CANCELLED", ptId: $ptId, locationId: $locationId }) {
              totalCount
            }
            archived: bookings(condition: { status: "ARCHIVED", locationId: $locationId }) {
              totalCount
            }
          }
      `,
      variables() {
        return { ptId: this.ptId, locationId: this.locationId };
      },
      skip() {
        return this.locationId === null;
      },
      update(data) {
        return data;
      }
    },

    ptLocationList: {
      fetchPolicy: "no-cache",
      query: gql`
      query PTLocations {
        ptLocationList {
          name
          id
        }
      }`,
    },
  },
};

const bookingCommonQuery = `
  id
  createdAt
  location {
    name
  }
  membership {
    status
    smsByReceiverList(orderBy: CREATED_AT_ASC) {
      id
      msg
      createdAt
      status
    }
    product {
      name
      salesPeriodBeginsAt
      salesPeriodEndsAt
    }
    promotion {
      name
      salesPeriodBeginsAt
      salesPeriodEndsAt
      type
    }
    id
    customer {
      firstName
      gender
      birthdate
      lastName
      mobileNumber
      mobilePrefix
      username
    }
  }
  status
  ptId
  type
  hours
  messagesList(orderBy: CREATED_AT_ASC) {
    id
    msg
    createdAt
    sender
    fromList {
      id
      firstName
      lastName
    }
  }
  packageOrder {
    package {
      title
    }
  }
`;
const bookingQuery = gql`
query BookingQuery(
  $status: String!,
  $offset: Int!,
  $first: Int!,
  $ptId: UUID,
  $locationId: UUID
) {
  __typename
  bookingsList(
    first: $first
    offset: $offset
    orderBy: CREATED_AT_DESC
    condition: { status: $status, ptId: $ptId, locationId: $locationId }
  ) {
    ${ bookingCommonQuery }
  }
}
`;

const bookingSearchQuery = gql`
query BookingSearch ($q: String!, $status: String!) {
  __typename
  bookingSearchList(q: $q, _status: $status) {
    ${ bookingCommonQuery }
  }
}
`
</script>

<style lang="scss" scoped>
@import "@/assets/style/responsive";

.Bookings {
  .bookings-list {
    max-width: 1024px;
  }

  .load {
    padding: 1em;
    display: flex;
    justify-content: center;
  }

  .center {
    display: flex;
    justify-content: center;
  }

  select {
    color: var(--blue-purple);
    border-color: var(--blue-purple);
    font-weight: bold;
  }

  select.status {
    display: none;
  }

  .filters {
    display: flex;
    align-items: center;
    padding-bottom: 10px;
  }

  .status-container {
    flex: 2 1;
  }

  select.location {
    flex: 1 3;
  }

  .membership {
    border-bottom: solid 1px rgba(151, 151, 151, 0.37);
  }

  .list-enter-active,
  .list-leave-active {
    transition: all 100ms;
  }

  .list-enter,
  .list-leave-to {
    opacity: 0;
  }

  .search {
    display: flex;
    align-items: center;
    padding: 1em;
    padding-right: 0;
    justify-content: flex-start;

    form {
      display: flex;
      align-items: center;
    }

    .clear-search {
      cursor: pointer;
      z-index: 1;
    }

    input {
      flex: 0;
      border: 0;
      box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.3);
    }

    svg {
      position: relative;
      right: 2em;
    }
  }

  @include phone {
    .Tabs {
      display: none;
    }
    select.status {
      display: inherit;
      width: 100%;
    }

    .title {
      padding-bottom: 1em;
    }

    .search input {
      flex: 1;
    }
  }
}
</style>
